import React from "react";
import { Box, Button, Paper, Slide, Typography, useMediaQuery, useTheme } from "@mui/material";
import CookieIcon from "@mui/icons-material/Cookie";
import { useTranslation } from "react-i18next";
import { useCookieConsent } from "../../contexts/CookieConsentContext";

const SIDEBAR_WIDTH = 300;

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const { showBanner, acceptAll, rejectAll, setShowPreferences } = useCookieConsent();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  if (!showBanner) return null;

  return (
    <Slide direction="up" in={showBanner} mountOnEnter unmountOnExit>
      <Paper
        elevation={8}
        role="region"
        aria-label={t("cookie-consent-title")}
        sx={{
          position: "fixed",
          bottom: 0,
          left: isDesktop ? SIDEBAR_WIDTH : 0,
          right: 0,
          zIndex: 1050,
          borderTop: "3px solid var(--primary-main, var(--main-color))",
          borderRadius: 0,
          p: { xs: "12px 12px max(12px, env(safe-area-inset-bottom))", sm: 2.5, md: 3 },
          maxHeight: { xs: "70dvh", sm: "none" },
          overflowY: "auto",
          boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: { xs: 1.25, sm: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: 1, sm: 2 }, flex: 1 }}>
            <CookieIcon color="primary" sx={{ fontSize: { xs: 22, sm: 32 }, mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.25, fontSize: { xs: "0.9375rem", sm: "1.125rem" } }}>
                {t("cookie-consent-title")}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, lineHeight: { xs: 1.35, sm: 1.5 } }}>
                {t("cookie-consent-message")}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: { xs: "grid", sm: "flex" },
              gridTemplateColumns: { xs: "1fr 1fr", sm: "none" },
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Button variant="outlined" onClick={rejectAll} size="small" data-testid="cookie-reject-all">
              {t("cookie-consent-reject-all")}
            </Button>
            <Button variant="contained" onClick={acceptAll} size="small" data-testid="cookie-accept-all">
              {t("cookie-consent-accept-all")}
            </Button>
            <Button
              variant="text"
              onClick={() => setShowPreferences(true)}
              size="small"
              sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
              data-testid="cookie-customize"
            >
              {t("cookie-consent-customize")}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default CookieConsentBanner;
